import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { Lotto, LottoEntry } from '../types/lotto';
import { getLottos, createLotto as createLottoApi, applyLotto, drawLotto } from '../utils/lottoApi';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

interface LottoContextType {
  lottos: Lotto[];
  isLoading: boolean;
  drawResult: {
    winners: any[];
    losers: any[];
  } | null;
  showAnimation: boolean;
  loadLottos: () => Promise<void>;
  addLotto: (date: Date, maxSlots: number) => Promise<void>;
  applyToLotto: (lottoId: string, reason: string) => Promise<void>;
  completeLotto: (lottoId: string) => Promise<void>;
  closeAnimation: () => void;
}

const LottoContext = createContext<LottoContextType | undefined>(undefined);

export const LottoProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [lottos, setLottos] = useState<Lotto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [drawResult, setDrawResult] = useState<{ winners: any[]; losers: any[] } | null>(null);
  const [showAnimation, setShowAnimation] = useState(false);

  // 로또 목록 불러오기
  const loadLottos = async () => {
    if (!isAuthenticated) return;

    setIsLoading(true);
    try {
      console.log('🔄 로또 목록 로드 시작...');
      const response = await getLottos();
      console.log('📦 API 응답:', response);
      
      if (response.success && response.data?.lottos) {
        console.log('✅ 로또 목록 설정:', response.data.lottos);
        setLottos(response.data.lottos);
      } else {
        console.warn('⚠️ 응답 형식 이상:', response);
      }
    } catch (error) {
      console.error('❌ Failed to load lottos:', error);
      toast.error('로또 목록을 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // 로그인 시 자동 로드
  useEffect(() => {
    if (isAuthenticated) {
      console.log('🎯 useEffect 트리거 - 로또 로드 시작');
      loadLottos();
    } else {
      console.log('🚫 로그인 안 됨 - 로또 로드 건너뜀');
    }
  }, [isAuthenticated]); // loadLottos는 의도적으로 제외 (무한 루프 방지)

  // 로또 생성 (관리자)
  const addLotto = async (date: Date, maxSlots: number) => {
    if (!isAuthenticated) {
      toast.error('로그인이 필요합니다.');
      return;
    }

    try {
      const response = await createLottoApi(date, maxSlots);
      
      if (response.success) {
        toast.success('로또가 생성되었습니다! 🎉');
        await loadLottos();
      }
    } catch (error: any) {
      const message = error.response?.data?.message || '로또 생성에 실패했습니다.';
      toast.error(message);
      throw error;
    }
  };

  // 로또 신청
  const applyToLotto = async (lottoId: string, reason: string) => {
    if (!isAuthenticated) {
      toast.error('로그인이 필요합니다.');
      return;
    }

    try {
      const response = await applyLotto(lottoId, reason);
      
      if (response.success) {
        toast.success('로또 신청이 완료되었습니다! 🎉');
        await loadLottos();
      }
    } catch (error: any) {
      const message = error.response?.data?.message || '로또 신청에 실패했습니다.';
      toast.error(message);
      throw error;
    }
  };

  // 로또 추첨 (관리자)
  const completeLotto = async (lottoId: string) => {
    if (!isAuthenticated) {
      toast.error('로그인이 필요합니다.');
      return;
    }

    try {
      const response = await drawLotto(lottoId);
      
      if (response.success) {
        const { winners = [], losers = [] } = response.data || {};
        
        // 추첨 결과 저장 + 애니메이션 표시
        setDrawResult({ winners, losers });
        setShowAnimation(true);
        
        // 로또 목록 새로고침
        await loadLottos();
      }
    } catch (error: any) {
      const message = error.response?.data?.message || '추첨에 실패했습니다.';
      toast.error(message);
      throw error;
    }
  };

  // 애니메이션 닫기
  const closeAnimation = useCallback(() => {
    setShowAnimation(false);
    
    // 애니메이션 종료 후 당첨/탈락 토스트
    if (drawResult) {
      const currentUserId = user?.id;
      const { winners, losers } = drawResult;
      
      if (currentUserId) {
        const isWinner = winners.some((w: any) => w.userId === currentUserId);
        const isLoser = losers.some((l: any) => l.userId === currentUserId);
        
        if (isLoser) {
          toast('😢 아쉽게 탈락하셨습니다. 페어니스 포인트 +10P 적립!', {
            duration: 5000,
            icon: '💪',
            style: {
              background: '#FEF3C7',
              color: '#92400E',
            },
          });
        }
      }
    }
    
    setDrawResult(null);
  }, [drawResult, user?.id]);

  const value: LottoContextType = {
    lottos,
    isLoading,
    drawResult,
    showAnimation,
    loadLottos,
    addLotto,
    applyToLotto,
    completeLotto,
    closeAnimation,
  };

  return (
    <LottoContext.Provider value={value}>
      {children}
    </LottoContext.Provider>
  );
};

export const useLotto = (): LottoContextType => {
  const context = useContext(LottoContext);
  if (!context) {
    throw new Error('useLotto must be used within LottoProvider');
  }
  return context;
};
